import React from 'react';
import { DentalSeverityBadge } from '../DentalSeverityBadge';
import { STATUS_CONFIGS, isSentinelSeverity } from '../../lib/riskConstants';

const KANBAN_COLUMNS = ['pending', 'investigating', 'resolved', 'closed'];

/**
 * Modern Kanban Board for Dental Risk Management
 */
export default function RiskKanbanBoard({
  reports = [],
  onSelectCase,
  onQuickStatusMove,
  isMobile = false
}) {
  return (
    <div className="risk-kanban-board">
      {KANBAN_COLUMNS.map((colStatus) => {
        const cfg = STATUS_CONFIGS[colStatus];
        const colReports = reports.filter((r) => r.status === colStatus);

        return (
          <div
            key={colStatus}
            className="risk-kanban-col"
            style={{ borderColor: cfg.border }}
          >
            {/* Column Header */}
            <div
              className="risk-kanban-col-header"
              style={{ backgroundColor: cfg.bg, borderColor: cfg.border }}
            >
              <div className="risk-kanban-col-title" style={{ color: cfg.color }}>
                <span>{cfg.icon}</span>
                <span>{cfg.label}</span>
              </div>
              <span
                className="risk-kanban-col-count"
                style={{ color: cfg.color, borderColor: cfg.border }}
              >
                {colReports.length}
              </span>
            </div>

            {/* Column Cards Container */}
            <div className="risk-kanban-col-cards risk-scrollbar">
              {colReports.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '36px 12px',
                    color: '#94a3b8',
                    fontSize: '0.84em',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '24px', opacity: 0.6 }}>📭</span>
                  <span>ไม่มีเคสในสถานะนี้</span>
                </div>
              ) : (
                colReports.map((report) => {
                  const isSentinel = isSentinelSeverity(report.severity_level);
                  const imageCount = report.image_urls ? report.image_urls.length : 0;

                  return (
                    <div
                      key={report.id || report.incident_number}
                      className={`risk-case-card ${isSentinel ? 'risk-case-card--sentinel' : ''}`}
                      onClick={() => onSelectCase?.(report)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          onSelectCase?.(report);
                        }
                      }}
                    >
                      {/* Header: Incident No + Severity Badge */}
                      <div className="risk-case-card__header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.78em', fontWeight: '700', color: '#64748b' }}>
                            {report.incident_number}
                          </span>
                          {report.risk_domain === 'cssd' && (
                            <span
                              style={{
                                fontSize: '0.68em',
                                fontWeight: '700',
                                backgroundColor: '#ccfbf1',
                                color: '#0f766e',
                                padding: '1px 5px',
                                borderRadius: '4px'
                              }}
                            >
                              🧼 CSSD
                            </span>
                          )}
                          {report.recall_batch_triggered && (
                            <span
                              style={{
                                fontSize: '0.68em',
                                fontWeight: '800',
                                backgroundColor: '#fee2e2',
                                color: '#dc2626',
                                padding: '1px 5px',
                                borderRadius: '4px'
                              }}
                            >
                              🚨 RECALL
                            </span>
                          )}
                        </div>
                        <DentalSeverityBadge level={report.severity_level} domain={report.risk_domain} size="sm" />
                      </div>

                      {/* Title */}
                      <div className="risk-case-card__title">
                        {report.title}
                      </div>

                      {/* Description Snippet */}
                      <div className="risk-case-card__desc">
                        {report.description}
                      </div>

                      {/* Meta Info: Location, Patient, Photos */}
                      <div className="risk-case-card__meta">
                        <span>📍 {report.location}</span>
                        {report.is_patient_involved && (
                          <span style={{ color: '#0284c7', fontWeight: '600' }}>
                            👤 HN {report.patient_hn || 'มีผู้ป่วย'}
                          </span>
                        )}
                        {imageCount > 0 && (
                          <span>📷 {imageCount} ภาพ</span>
                        )}
                      </div>

                      {/* Quick Move Action in Kanban */}
                      <div
                        style={{
                          display: 'flex',
                          gap: '6px',
                          justifyContent: 'flex-end',
                          marginTop: '4px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {colStatus === 'pending' && (
                          <button
                            type="button"
                            className="risk-quick-btn"
                            onClick={() => onQuickStatusMove?.(report.id, 'investigating')}
                            title="ย้ายไป กำลังสอบสวน RCA"
                            style={{
                              backgroundColor: '#eff6ff',
                              color: '#2563eb',
                              borderColor: '#bfdbfe'
                            }}
                          >
                            เริ่ม RCA →
                          </button>
                        )}
                        {colStatus === 'investigating' && (
                          <button
                            type="button"
                            className="risk-quick-btn"
                            onClick={() => onQuickStatusMove?.(report.id, 'resolved')}
                            title="ย้ายไป ดำเนินการแก้ไขแล้ว"
                            style={{
                              backgroundColor: '#f0fdf4',
                              color: '#16a34a',
                              borderColor: '#bbf7d0'
                            }}
                          >
                            แก้ไขแล้ว →
                          </button>
                        )}
                        {colStatus === 'resolved' && (
                          <button
                            type="button"
                            className="risk-quick-btn"
                            onClick={() => onQuickStatusMove?.(report.id, 'closed')}
                            title="ย้ายไป ปิดเคส"
                            style={{
                              backgroundColor: '#f1f5f9',
                              color: '#475569',
                              borderColor: '#cbd5e1'
                            }}
                          >
                            ปิดเคส ✓
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
